// CampaignManagerPOC.js — Documentation Fonctionnelle : POC « Campagnes » (module Campagne).
// Aperçu interactif de l'écran « Gestionnaire de campagnes » (bannière + liste filtrable maître /
// détail à onglets Vue d'ensemble / Cas de test / Exécutions + création + pilotage des statuts),
// doublé d'un guide utilisateur pas-à-pas. Les actions sont SIMULÉES localement (démo, aucun réseau).
// Source de vérité : automata-studio-frontend [CPG][02]-CampaignManager.js + automata_campaign
// (CampaignController, CampaignStatus, RecurrenceType, CampaignManagementService).
import React, { useState } from 'react';
import {
  Plus, X, Save, Calendar, Play, Pause, Check, Archive, FileCode, Activity, Info,
  Target, FileText, Tag, CheckCircle, XCircle, ArrowRight, ChevronUp, ChevronDown,
  CalendarClock, RefreshCw, Trash2, Copy, Edit, Search, BarChart2, ChevronRight, Settings, AlertTriangle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Tabs, Icon, SearchBar, EmptyState } from '../ui';

// 8 statuts de campagne (libellés FR exacts du back + couleur).
const STATUSES = {
  DRAFT: { label: 'Brouillon', color: '#6B7280', desc: 'Campagne en cours de création' },
  ACTIVE: { label: 'Active', color: '#10B981', desc: 'Campagne prête à être exécutée' },
  SCHEDULED: { label: 'Planifiée', color: '#3B82F6', desc: 'Planifiée pour exécution future' },
  IN_PROGRESS: { label: 'En cours', color: '#2563EB', desc: 'Au moins une exécution active' },
  ON_HOLD: { label: 'En attente', color: '#F59E0B', desc: 'Temporairement mise en attente' },
  COMPLETED: { label: 'Terminée', color: '#059669', desc: 'Toutes les exécutions terminées' },
  CANCELLED: { label: 'Annulée', color: '#EF4444', desc: 'Campagne annulée' },
  ARCHIVED: { label: 'Archivée', color: '#8B5CF6', desc: 'Campagne archivée' },
};
const statusOf = (s) => STATUSES[s] || { label: s, color: '#6B7280', desc: '' };

// 9 types de récurrence (libellés FR exacts du back).
const RECURRENCES = [
  { value: 'PONCTUEL', label: 'Ponctuel', desc: 'Exécution unique' },
  { value: 'RECURRENT', label: 'Récurrent', desc: 'Exécution récurrente planifiée' },
  { value: 'PLANIFIE', label: 'Planifié', desc: 'À une date/heure précise (non récurrente)' },
  { value: 'A_LA_DEMANDE', label: 'À la demande', desc: 'Déclenchée manuellement' },
  { value: 'EVENT_DRIVEN', label: 'Événementiel', desc: 'Déclenchée par un événement externe' },
  { value: 'POST_DEPLOIEMENT', label: 'Post-déploiement', desc: 'Après un déploiement' },
  { value: 'HEBDOMADAIRE', label: 'Hebdomadaire', desc: 'Exécution hebdomadaire' },
  { value: 'JOURNALIER', label: 'Journalier', desc: 'Exécution quotidienne' },
  { value: 'MENSUEL', label: 'Mensuel', desc: 'Exécution mensuelle' },
];
const recurrenceOf = (v) => RECURRENCES.find((r) => r.value === v) || { label: v, desc: '' };

// Jeu de données fictif (démo).
const SAMPLE = [
  {
    id: 1, campaignId: 'CPG-7F3A21B9', name: 'Recette Virements SEPA', status: 'ACTIVE',
    description: 'Validation de bout en bout des virements SEPA après migration AMPLITUDE v11.',
    objective: 'Garantir la non-régression du parcours virement émis / reçu.',
    recurrenceType: 'PONCTUEL', priority: 8, isActive: true, tags: 'sepa, virement, recette',
    creationDate: '2026-06-02', startDate: '2026-06-03', endDate: '2026-06-20', createdBy: 'admin',
    testCases: [
      { id: 11, name: 'Virement SEPA standard', testCaseId: 'TC-001', description: 'Émission virement nominal', critical: true, order: 1 },
      { id: 12, name: 'Virement avec IBAN invalide', testCaseId: 'TC-002', description: 'Contrôle de rejet IBAN', critical: true, order: 2 },
      { id: 13, name: 'Consultation solde après virement', testCaseId: 'TC-003', description: 'Vérification du solde', critical: false, order: 3 },
    ],
    executions: [
      { id: 101, executionId: 'EXE-9001', name: 'Exécution nocturne', status: 'COMPLETED_SUCCESS', passed: 3, failed: 0, skipped: 0, executedBy: 'scheduler', date: '2026-06-13' },
      { id: 102, executionId: 'EXE-9002', name: 'Exécution manuelle', status: 'COMPLETED_FAILURE', passed: 2, failed: 1, skipped: 0, executedBy: 'admin', date: '2026-06-14' },
    ],
  },
  {
    id: 2, campaignId: 'CPG-1C5E80D4', name: 'Compensation ACH CEMAC', status: 'SCHEDULED',
    description: 'Campagne de validation de la compensation automatique CEMAC.',
    objective: 'Vérifier les inversions, retours et annulations.',
    recurrenceType: 'HEBDOMADAIRE', priority: 5, isActive: true, tags: 'ach, cemac',
    creationDate: '2026-06-05', startDate: '2026-06-16', endDate: '', createdBy: 'admin',
    testCases: [
      { id: 21, name: 'Prélèvement standard', testCaseId: 'TC-101', description: 'Prélèvement nominal', critical: true, order: 1 },
      { id: 22, name: 'Inversion de prélèvement', testCaseId: 'TC-102', description: 'Inversion R-message', critical: false, order: 2 },
    ],
    executions: [],
  },
  {
    id: 3, campaignId: 'CPG-44A9F207', name: 'Smoke test quotidien', status: 'DRAFT',
    description: '', objective: 'Sanity-check rapide des écrans principaux.',
    recurrenceType: 'JOURNALIER', priority: 2, isActive: true, tags: 'smoke, daily',
    creationDate: '2026-06-12', startDate: '', endDate: '', createdBy: 'ops',
    testCases: [],
    executions: [],
  },
  {
    id: 4, campaignId: 'CPG-08B6E1AA', name: 'Migration AMPLITUDE — Phase 1', status: 'COMPLETED',
    description: 'Recette complète de la phase 1 de migration.',
    objective: 'Couverture exhaustive des fonctions migrées.',
    recurrenceType: 'PONCTUEL', priority: 10, isActive: false, tags: 'migration, phase1',
    creationDate: '2026-05-10', startDate: '2026-05-11', endDate: '2026-05-30', createdBy: 'admin',
    testCases: [
      { id: 41, name: 'Ouverture de compte', testCaseId: 'TC-301', description: 'Création client + compte', critical: true, order: 1 },
    ],
    executions: [
      { id: 401, executionId: 'EXE-8800', name: 'Exécution finale', status: 'COMPLETED_SUCCESS', passed: 1, failed: 0, skipped: 0, executedBy: 'admin', date: '2026-05-29' },
    ],
  },
];

const EMPTY_FORM = { name: '', description: '', objective: '', recurrenceType: 'PONCTUEL', priority: 0, startDate: '', endDate: '', tags: '', isActive: true };

const EXEC_STATUS = {
  COMPLETED_SUCCESS: { label: 'Réussi', color: '#10B981', icon: 'CheckCircle' },
  COMPLETED_FAILURE: { label: 'Échec', color: '#EF4444', icon: 'XCircle' },
  RUNNING: { label: 'En cours', color: '#3B82F6', icon: 'Play' },
  PAUSED: { label: 'En pause', color: '#F59E0B', icon: 'Pause' },
  PENDING: { label: 'En attente', color: '#6B7280', icon: 'Clock' },
};
const execStatusOf = (s) => EXEC_STATUS[s] || EXEC_STATUS.PENDING;

const STATUS_FILTERS = [
  ['', 'Tous'], ['DRAFT', 'Brouillon'], ['ACTIVE', 'Active'], ['SCHEDULED', 'Planifiée'],
  ['IN_PROGRESS', 'En cours'], ['ON_HOLD', 'En attente'], ['COMPLETED', 'Terminée'],
  ['CANCELLED', 'Annulée'], ['ARCHIVED', 'Archivée'],
];

// ---- Petits sous-composants de la réplique ---------------------------------
const StatusBadge = ({ status }) => {
  const s = statusOf(status);
  return <Badge color={s.color}>{s.label}</Badge>;
};

const GuideStep = ({ n, children }) => (
  <li className="flex items-start gap-3">
    <span className="shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">{n}</span>
    <span className="text-sm text-gray-700 dark:text-gray-300">{children}</span>
  </li>
);

const InfoRow = ({ label, value, accent }) => (
  <div>
    <p className="text-xs font-medium text-gray-400 dark:text-gray-500 mb-0.5">{label}</p>
    <div className={`text-sm ${accent ? 'text-gray-900 dark:text-gray-100 font-medium' : 'text-gray-800 dark:text-gray-200'}`}>{value}</div>
  </div>
);

// Barre de progression réussis / échecs / ignorés (réplique de MiniProgressBar).
const MiniProgress = ({ passed = 0, failed = 0, skipped = 0 }) => {
  const total = passed + failed + skipped;
  if (total === 0) return <span className="text-xs text-gray-400">—</span>;
  const pPct = (passed / total) * 100;
  const fPct = (failed / total) * 100;
  return (
    <div className="flex items-center gap-2.5">
      <div className="flex-1 h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden flex min-w-[60px]">
        {pPct > 0 && <div className="h-full bg-emerald-500" style={{ width: `${pPct}%` }} />}
        {fPct > 0 && <div className="h-full bg-red-500" style={{ width: `${fPct}%` }} />}
        {100 - pPct - fPct > 0 && <div className="h-full bg-amber-400" style={{ width: `${100 - pPct - fPct}%` }} />}
      </div>
      <span className="text-xs text-gray-500 whitespace-nowrap tabular-nums">{passed}/{total}</span>
    </div>
  );
};

// ---- Page -------------------------------------------------------------------
const CampaignManagerPOC = () => {
  const [campaigns, setCampaigns] = useState(SAMPLE);
  const [selectedId, setSelectedId] = useState(SAMPLE[0].id);
  const [mode, setMode] = useState('detail'); // 'detail' | 'create'
  const [tab, setTab] = useState('overview'); // 'overview' | 'testcases' | 'executions'
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [toast, setToast] = useState('');

  const selected = campaigns.find((c) => c.id === selectedId) || null;
  const filtered = campaigns.filter((c) =>
    (statusFilter === '' || c.status === statusFilter) &&
    (`${c.name} ${c.campaignId} ${c.description}`.toLowerCase().includes(search.toLowerCase()))
  );

  const flash = (msg) => { setToast(msg); window.setTimeout(() => setToast(''), 2200); };

  const openCreate = () => { setForm(EMPTY_FORM); setMode('create'); };
  const submitCreate = () => {
    if (!form.name) return;
    const id = Math.max(0, ...campaigns.map((c) => c.id)) + 1;
    const created = {
      ...form, id, campaignId: `CPG-${Math.random().toString(16).slice(2, 10).toUpperCase()}`,
      status: 'DRAFT', priority: parseInt(form.priority, 10) || 0, creationDate: '2026-06-16',
      createdBy: 'vous', testCases: [], executions: [],
    };
    setCampaigns((prev) => [...prev, created]);
    setSelectedId(id); setMode('detail'); setTab('overview');
    flash('Campagne créée (statut Brouillon).');
  };

  const setStatus = (status) => {
    setCampaigns((prev) => prev.map((c) => c.id === selectedId ? { ...c, status } : c));
    flash(`Statut mis à jour : ${statusOf(status).label}.`);
  };
  const toggleActive = () => {
    setCampaigns((prev) => prev.map((c) => c.id === selectedId ? { ...c, isActive: !c.isActive } : c));
  };
  // Réordonnancement local d'un cas de test (simulé) — réplique des flèches haut/bas.
  const moveTestCase = (index, direction) => {
    setCampaigns((prev) => prev.map((c) => {
      if (c.id !== selectedId) return c;
      const list = [...c.testCases];
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= list.length) return c;
      [list[index], list[target]] = [list[target], list[index]];
      return { ...c, testCases: list.map((tc, i) => ({ ...tc, order: i + 1 })) };
    }));
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e && e.target ? e.target.value : e }));
  const tags = (s) => (s || '').split(',').map((t) => t.trim()).filter(Boolean);

  return (
    <div className="space-y-8">
      {/* (1) Intro */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Documentation Fonctionnelle — Campagnes</h1>
          <Badge color="#F59E0B">Module Campagne</Badge>
          <Badge color="#3B82F6">POC interactif</Badge>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-300">Aperçu interactif de l'écran <strong>Gestionnaire de campagnes</strong> (démo, actions simulées localement).</p>
      </div>

      {/* (2) Aperçu interactif de l'IHM ===================================== */}
      {/* Bannière dégradée (carte distincte, comme l'écran réel) */}
      <div className="rounded-2xl bg-gradient-to-r from-violet-500 to-purple-700 p-5 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center"><Calendar className="h-5 w-5 text-white" /></div>
            <div>
              <h2 className="text-xl font-bold text-white">Gestionnaire de campagnes</h2>
              <p className="text-violet-200 text-sm">Créer, organiser et piloter vos campagnes de test</p>
            </div>
          </div>
          <Button onClick={openCreate} className="bg-white/20 hover:bg-white/30 text-white"><Plus className="h-4 w-4" /><span className="hidden sm:inline">Nouvelle campagne</span></Button>
        </div>
      </div>

      {/* Coquille maître / détail */}
      <Card className="overflow-hidden">
        {toast && (
          <div className="px-5 py-2 bg-emerald-50 dark:bg-emerald-500/10 border-b border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300 text-sm flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />{toast}
          </div>
        )}

        <div className="flex flex-col lg:flex-row">
          {/* Panneau gauche : liste (sidebar) */}
          <div className="lg:w-80 shrink-0 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 flex flex-col">
            <div className="p-3 border-b border-gray-200 dark:border-gray-700 space-y-2.5">
              <SearchBar value={search} onChange={setSearch} placeholder="Rechercher..." />
              <div className="flex items-center gap-1.5 flex-wrap">
                {STATUS_FILTERS.map(([k, lbl]) => (
                  <button key={k || 'all'} onClick={() => setStatusFilter(k === statusFilter ? '' : k)} className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${statusFilter === k ? 'bg-violet-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'}`}>{lbl}</button>
                ))}
                <button onClick={() => flash('Actualisation simulée.')} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ml-auto shrink-0"><RefreshCw className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700/50">
              <span className="text-xs text-gray-400">{filtered.length} campagne{filtered.length > 1 ? 's' : ''}</span>
            </div>
            <div className="flex-1 overflow-y-auto max-h-[480px]">
              {filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-400 px-4">
                  <Icon name="FolderOpen" className="h-9 w-9 opacity-50 mb-2" />
                  <p className="text-sm font-medium">Aucune campagne</p>
                  {(search || statusFilter) && <button onClick={() => { setSearch(''); setStatusFilter(''); }} className="mt-2 text-xs text-violet-500 hover:underline">Effacer les filtres</button>}
                </div>
              ) : (
                <div className="py-1">
                  {filtered.map((c) => {
                    const on = selected?.id === c.id && mode === 'detail';
                    return (
                      <div key={c.id} onClick={() => { setSelectedId(c.id); setMode('detail'); setTab('overview'); }}
                        className={`px-4 py-3 cursor-pointer border-b border-gray-100 dark:border-gray-700/50 transition-colors ${on ? 'bg-violet-50 dark:bg-violet-900/20 border-l-4 border-l-violet-500' : 'hover:bg-gray-50 dark:hover:bg-gray-700/30 border-l-4 border-l-transparent'}`}>
                        <p className="font-medium text-sm text-gray-800 dark:text-gray-100 truncate">{c.name}</p>
                        <div className="flex items-center justify-between mt-1.5">
                          <span className="text-xs text-gray-400">{c.campaignId}</span>
                          <StatusBadge status={c.status} />
                        </div>
                        <div className="flex items-center gap-1 mt-1.5">
                          <FileCode className="h-3 w-3 text-gray-400" />
                          <span className="text-[10px] text-gray-400">{c.testCases.length} cas de test</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Panneau droit : détail ou création */}
          <div className="flex-1 p-5 min-w-0 bg-gray-50/50 dark:bg-gray-900/40">
            {mode === 'create' ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2"><Plus className="h-5 w-5 text-violet-500" />Nouvelle campagne</h3>
                  <button onClick={() => setMode('detail')} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><X className="h-5 w-5" /></button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <label className="text-sm">
                    <span className="block text-gray-700 dark:text-gray-300 mb-1">Nom de la campagne <span className="text-red-500">*</span></span>
                    <input value={form.name} onChange={set('name')} placeholder="Recette Virements SEPA" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-violet-500 focus:outline-none" />
                  </label>
                  <label className="text-sm">
                    <span className="block text-gray-700 dark:text-gray-300 mb-1">Type de récurrence</span>
                    <select value={form.recurrenceType} onChange={set('recurrenceType')} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-violet-500">
                      {RECURRENCES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </label>
                  <label className="text-sm sm:col-span-2">
                    <span className="block text-gray-700 dark:text-gray-300 mb-1">Description</span>
                    <textarea value={form.description} onChange={set('description')} rows={3} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-violet-500 focus:outline-none" />
                  </label>
                  <label className="text-sm sm:col-span-2">
                    <span className="block text-gray-700 dark:text-gray-300 mb-1">Objectif</span>
                    <textarea value={form.objective} onChange={set('objective')} rows={2} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-violet-500 focus:outline-none" />
                  </label>
                  <label className="text-sm">
                    <span className="block text-gray-700 dark:text-gray-300 mb-1">Date de début</span>
                    <input type="date" value={form.startDate} onChange={set('startDate')} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-violet-500" />
                  </label>
                  <label className="text-sm">
                    <span className="block text-gray-700 dark:text-gray-300 mb-1">Date de fin</span>
                    <input type="date" value={form.endDate} onChange={set('endDate')} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-violet-500" />
                  </label>
                  <label className="text-sm">
                    <span className="block text-gray-700 dark:text-gray-300 mb-1">Priorité (0–10)</span>
                    <input type="number" min={0} max={10} value={form.priority} onChange={set('priority')} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-violet-500" />
                  </label>
                  <label className="text-sm">
                    <span className="block text-gray-700 dark:text-gray-300 mb-1">Tags (séparés par des virgules)</span>
                    <input value={form.tags} onChange={set('tags')} placeholder="tag1, tag2" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-violet-500" />
                  </label>
                </div>
                {tags(form.tags).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {tags(form.tags).map((t, i) => <Badge key={i} color="#8B5CF6"><Tag className="h-3 w-3" />{t}</Badge>)}
                  </div>
                )}
                <div className="flex items-center justify-between p-3 rounded-xl bg-gray-100 dark:bg-gray-700/50 mt-4">
                  <span className="text-sm text-gray-700 dark:text-gray-300">Campagne active</span>
                  <button onClick={() => setForm((f) => ({ ...f, isActive: !f.isActive }))} className={`relative w-11 h-6 rounded-full transition-colors ${form.isActive ? 'bg-violet-500' : 'bg-gray-300 dark:bg-gray-600'}`}>
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${form.isActive ? 'translate-x-5' : 'translate-x-0'}`} />
                  </button>
                </div>
                <div className="flex justify-end gap-2 mt-5">
                  <Button variant="ghost" onClick={() => setMode('detail')}>Annuler</Button>
                  <Button onClick={submitCreate} disabled={!form.name} className="bg-gradient-to-r from-violet-500 to-purple-600 text-white"><Save className="h-4 w-4" />Créer</Button>
                </div>
                {!form.name && <p className="text-xs text-gray-400 mt-2">Le <strong>Nom</strong> est obligatoire. La campagne est créée au statut <strong>Brouillon</strong>.</p>}
              </div>
            ) : selected ? (
              <div>
                {/* En-tête détail */}
                <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">{selected.name}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{selected.campaignId} — créée le {selected.creationDate}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => flash('Modification simulée.')}><Edit className="h-4 w-4" /><span className="hidden sm:inline">Modifier</span></Button>
                    <Button variant="outline" size="sm" onClick={() => flash('Duplication simulée.')} className="text-violet-600 border-violet-200 dark:border-violet-500/40"><Copy className="h-4 w-4" /><span className="hidden sm:inline">Dupliquer</span></Button>
                    <Button variant="outline" size="sm" onClick={() => flash('Suppression simulée.')} className="text-red-500 border-red-200 dark:border-red-500/40"><Trash2 className="h-4 w-4" /><span className="hidden sm:inline">Supprimer</span></Button>
                    {selected.status === 'ACTIVE' && <Button size="sm" onClick={() => { setTab('executions'); flash('Centre d\'exécution (simulé).'); }} className="bg-gradient-to-r from-violet-500 to-purple-600 text-white"><Play className="h-4 w-4" />Exécuter</Button>}
                  </div>
                </div>

                {/* Barre de statut + actions de transition */}
                <div className="flex items-center flex-wrap gap-2 mb-4">
                  <StatusBadge status={selected.status} />
                  {selected.status === 'DRAFT' && <Button variant="ghost" size="sm" onClick={() => setStatus('ACTIVE')} className="text-emerald-600 bg-emerald-100 dark:bg-emerald-500/15"><Check className="h-4 w-4" />Activer</Button>}
                  {selected.status === 'ACTIVE' && <Button variant="ghost" size="sm" onClick={() => setStatus('ON_HOLD')} className="text-amber-600 bg-amber-100 dark:bg-amber-500/15"><Pause className="h-4 w-4" />Mettre en attente</Button>}
                  {selected.status === 'ON_HOLD' && <Button variant="ghost" size="sm" onClick={() => setStatus('ACTIVE')} className="text-emerald-600 bg-emerald-100 dark:bg-emerald-500/15"><Play className="h-4 w-4" />Reprendre</Button>}
                  {(selected.status === 'ACTIVE' || selected.status === 'ON_HOLD') && <Button variant="ghost" size="sm" onClick={() => setStatus('CANCELLED')} className="text-red-600 bg-red-100 dark:bg-red-500/15"><X className="h-4 w-4" />Annuler</Button>}
                  {selected.status === 'COMPLETED' && <Button variant="ghost" size="sm" onClick={() => setStatus('ARCHIVED')} className="text-purple-600 bg-purple-100 dark:bg-purple-500/15"><Archive className="h-4 w-4" />Archiver</Button>}
                </div>

                <Tabs
                  tabs={[
                    { id: 'overview', label: "Vue d'ensemble" },
                    { id: 'testcases', label: 'Cas de test', count: selected.testCases.length },
                    { id: 'executions', label: 'Exécutions', count: selected.executions.length },
                  ]}
                  active={tab} onChange={setTab} className="mb-4"
                />

                {tab === 'overview' && (
                  <div className="space-y-4">
                    {/* Description + Objectif */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="p-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2"><FileText className="h-4 w-4 text-violet-500" />Description</div>
                        <p className={`text-sm ${selected.description ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 italic'}`}>{selected.description || 'Aucune description'}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2"><Target className="h-4 w-4 text-violet-500" />Objectif</div>
                        <p className={`text-sm ${selected.objective ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 italic'}`}>{selected.objective || 'Aucun objectif'}</p>
                      </div>
                    </div>

                    {/* Détails */}
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
                      <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3"><Info className="h-4 w-4 text-blue-500" />Détails</div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        <InfoRow label="Date de création" value={selected.creationDate} />
                        <InfoRow label="Date de début" value={selected.startDate || <span className="text-gray-400 italic">Non définie</span>} />
                        <InfoRow label="Date de fin" value={selected.endDate || <span className="text-gray-400 italic">Non définie</span>} />
                        <InfoRow label="Récurrence" value={recurrenceOf(selected.recurrenceType).label} accent />
                        <InfoRow label="Priorité" value={selected.priority} accent />
                        <InfoRow
                          label="Activité"
                          value={selected.isActive
                            ? <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"><CheckCircle className="h-3 w-3" />Actif</span>
                            : <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400"><XCircle className="h-3 w-3" />Inactif</span>}
                        />
                        <InfoRow label="Créée par" value={selected.createdBy} accent />
                        <div className="col-span-full">
                          <p className="text-xs font-medium text-gray-400 dark:text-gray-500 mb-1">Tags</p>
                          <div className="flex flex-wrap gap-1.5">
                            {tags(selected.tags).length > 0
                              ? tags(selected.tags).map((t, i) => <Badge key={i} color="#8B5CF6"><Tag className="h-3 w-3" />{t}</Badge>)
                              : <span className="text-xs text-gray-400 italic">Aucun tag</span>}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Cartes d'action : Cas de test / Exécutions / Rapports */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center"><FileCode className="h-4 w-4 text-white" /></div>
                            <span className="font-semibold text-sm text-gray-700 dark:text-gray-200">Cas de test</span>
                          </div>
                          <span className="text-2xl font-bold text-gray-800 dark:text-gray-100">{selected.testCases.length}</span>
                        </div>
                        <p className="text-xs text-gray-400 mb-3">{selected.testCases.length === 0 ? 'Aucun associé' : `${selected.testCases.filter((t) => t.critical).length} critique(s)`}</p>
                        <button onClick={() => setTab('testcases')} className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border border-violet-300 dark:border-violet-500/40 text-violet-600 dark:text-violet-400 text-xs font-medium hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors">Gérer <ChevronRight className="h-3.5 w-3.5" /></button>
                      </div>
                      <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center"><Play className="h-4 w-4 text-white" /></div>
                          <span className="font-semibold text-sm text-gray-700 dark:text-gray-200">Exécutions</span>
                        </div>
                        <p className="text-xs text-gray-400 mb-3">Lancer ou consulter</p>
                        <button onClick={() => { setTab('executions'); flash('Centre d\'exécution (simulé).'); }} className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 text-white text-xs font-medium shadow hover:shadow-md transition-all"><Play className="h-3.5 w-3.5" />Centre d'exécution</button>
                      </div>
                      <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                        <div className="flex items-center gap-2 mb-3">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center"><BarChart2 className="h-4 w-4 text-white" /></div>
                          <span className="font-semibold text-sm text-gray-700 dark:text-gray-200">Rapports</span>
                        </div>
                        <p className="text-xs text-gray-400 mb-3">Générer et consulter</p>
                        <button onClick={() => flash('Ouverture des rapports (simulé).')} className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 text-xs font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"><BarChart2 className="h-3.5 w-3.5" />Rapports</button>
                      </div>
                    </div>

                    {/* Paramètres : campagne active */}
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
                      <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3"><Settings className="h-4 w-4 text-violet-500" />Paramètres</div>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-gray-700 dark:text-gray-300">Campagne active</div>
                          <div className="text-xs text-gray-400">Les campagnes inactives ne sont pas exécutées</div>
                        </div>
                        <button onClick={toggleActive} className={`relative w-11 h-6 rounded-full transition-colors ${selected.isActive ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'}`}>
                          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${selected.isActive ? 'translate-x-5' : 'translate-x-0'}`} />
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {tab === 'testcases' && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">Cas de test ({selected.testCases.length})</span>
                      <Button size="sm" onClick={() => flash('Ouverture du sélecteur de cas de test (simulé).')} className="bg-gradient-to-r from-violet-500 to-purple-600 text-white"><Plus className="h-4 w-4" />Ajouter</Button>
                    </div>
                    {selected.testCases.length === 0 ? (
                      <EmptyState icon={FileText} title="Aucun cas de test" message="Ajoutez des cas de test pour pouvoir lancer des exécutions." />
                    ) : (
                      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-300">
                            <tr>
                              <th className="px-3 py-2 text-xs font-semibold uppercase text-center w-16">Ordre</th>
                              <th className="px-4 py-2 text-xs font-semibold uppercase text-left">Nom</th>
                              <th className="px-4 py-2 text-xs font-semibold uppercase text-left hidden sm:table-cell">Description</th>
                              <th className="px-4 py-2 text-xs font-semibold uppercase text-left">Critique</th>
                              <th className="px-3 py-2 text-xs font-semibold uppercase text-center w-20 hidden sm:table-cell">Déplacer</th>
                              <th className="px-4 py-2 text-xs font-semibold uppercase text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
                            {selected.testCases.map((tc, idx) => (
                              <tr key={tc.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                <td className="px-3 py-2 text-center"><span className="inline-flex w-10 px-2 py-1 justify-center text-xs rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 tabular-nums">{tc.order}</span></td>
                                <td className="px-4 py-2"><p className="font-medium text-gray-800 dark:text-gray-100">{tc.name}</p><p className="text-xs text-gray-400">{tc.testCaseId}</p></td>
                                <td className="px-4 py-2 text-gray-600 dark:text-gray-400 truncate max-w-[300px] hidden sm:table-cell">{tc.description || '-'}</td>
                                <td className="px-4 py-2">{tc.critical ? <Badge color="#EF4444">Critique</Badge> : <span className="text-xs text-gray-400">Non</span>}</td>
                                <td className="px-3 py-2 text-center hidden sm:table-cell">
                                  <div className="flex items-center justify-center gap-0.5">
                                    <button onClick={() => moveTestCase(idx, 'up')} disabled={idx === 0} className="p-1 rounded text-gray-400 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20 disabled:opacity-30 transition-colors"><ChevronUp className="h-3.5 w-3.5" /></button>
                                    <button onClick={() => moveTestCase(idx, 'down')} disabled={idx === selected.testCases.length - 1} className="p-1 rounded text-gray-400 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20 disabled:opacity-30 transition-colors"><ChevronDown className="h-3.5 w-3.5" /></button>
                                  </div>
                                </td>
                                <td className="px-4 py-2 text-right"><button onClick={() => flash('Retrait simulé.')} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"><X className="h-3.5 w-3.5" />Retirer</button></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {tab === 'executions' && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">Exécutions ({selected.executions.length})</span>
                      <Button size="sm" onClick={() => flash('Lancement d\'exécution (simulé).')} className="bg-gradient-to-r from-violet-500 to-purple-600 text-white"><Play className="h-4 w-4" />Lancer une exécution</Button>
                    </div>
                    {selected.executions.length === 0 ? (
                      <EmptyState icon={Activity} title="Aucune exécution" message="Lancez une exécution pour voir les résultats ici." />
                    ) : (
                      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-300">
                            <tr>
                              <th className="px-4 py-2 text-xs font-semibold uppercase text-left">Exécution</th>
                              <th className="px-4 py-2 text-xs font-semibold uppercase text-left">Statut</th>
                              <th className="px-4 py-2 text-xs font-semibold uppercase text-left">Résultats</th>
                              <th className="px-4 py-2 text-xs font-semibold uppercase text-left hidden sm:table-cell">Exécuté par</th>
                              <th className="px-4 py-2 text-xs font-semibold uppercase text-right hidden md:table-cell">Date</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
                            {selected.executions.map((ex) => {
                              const es = execStatusOf(ex.status);
                              return (
                                <tr key={ex.id} onClick={() => flash('Ouverture de l\'exécution (simulé).')} className="hover:bg-violet-50/50 dark:hover:bg-violet-900/10 cursor-pointer transition-colors">
                                  <td className="px-4 py-2">
                                    <div className="flex items-center gap-2.5">
                                      <Icon name={es.icon} className="h-4 w-4" style={{ color: es.color }} />
                                      <div><div className="font-medium">{ex.name}</div><div className="text-xs text-gray-400">{ex.executionId}</div></div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-2"><Badge color={es.color}>{es.label}</Badge></td>
                                  <td className="px-4 py-2 min-w-[140px]"><MiniProgress passed={ex.passed} failed={ex.failed} skipped={ex.skipped} /></td>
                                  <td className="px-4 py-2 text-gray-500 hidden sm:table-cell">{ex.executedBy}</td>
                                  <td className="px-4 py-2 text-gray-500 text-right hidden md:table-cell">{ex.date}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-48 text-gray-400">
                <Icon name="FolderOpen" className="h-8 w-8 mb-3" />
                <p className="text-base font-medium">Aucune campagne sélectionnée</p>
                <p className="text-sm mt-1">Sélectionnez une campagne dans la liste.</p>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* (3) Guide utilisateur =========================================== */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Guide utilisateur</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Plus className="h-5 w-5 text-violet-500" />Créer une campagne</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Cliquer sur <strong>« Nouvelle campagne »</strong> (en haut à droite). <button onClick={openCreate} className="text-violet-600 dark:text-violet-400 underline">Essayer</button></GuideStep>
              <GuideStep n={2}>Saisir le <strong>Nom</strong> (obligatoire) puis la description et l'objectif.</GuideStep>
              <GuideStep n={3}>Choisir le <strong>Type de récurrence</strong> et la <strong>Priorité</strong> (0–10).</GuideStep>
              <GuideStep n={4}>Renseigner les <strong>Tags</strong> pour faciliter la recherche.</GuideStep>
              <GuideStep n={5}>Valider avec <strong>Créer</strong> — la campagne est créée au statut <strong>Brouillon</strong>.</GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><CalendarClock className="h-5 w-5 text-violet-500" />Planifier / Activer</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Sélectionner une campagne <strong>Brouillon</strong> dans la liste de gauche.</GuideStep>
              <GuideStep n={2}>Onglet <strong>Cas de test</strong> : ajouter et réordonner les cas à exécuter. <button onClick={() => { setMode('detail'); setTab('testcases'); }} className="text-violet-600 dark:text-violet-400 underline">Voir</button></GuideStep>
              <GuideStep n={3}>Choisir une récurrence planifiée (<strong>Hebdomadaire</strong>, <strong>Journalier</strong>, <strong>Mensuel</strong>…) pour un déclenchement automatique.</GuideStep>
              <GuideStep n={4}>Cliquer sur <strong>« Activer »</strong> : la campagne passe de Brouillon à <strong>Active</strong>. <button onClick={() => { setMode('detail'); setTab('overview'); }} className="text-violet-600 dark:text-violet-400 underline">Sélectionner une campagne</button></GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Play className="h-5 w-5 text-violet-500" />Lancer une exécution</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Sur une campagne <strong>Active</strong>, ouvrir l'onglet <strong>Exécutions</strong>. <button onClick={() => { setMode('detail'); setTab('executions'); }} className="text-violet-600 dark:text-violet-400 underline">Ouvrir</button></GuideStep>
              <GuideStep n={2}>Cliquer sur <strong>« Lancer une exécution »</strong> (ou le bouton <strong>Exécuter</strong> de l'en-tête).</GuideStep>
              <GuideStep n={3}>Suivre l'avancement et lire les <strong>résultats</strong> (réussis / échecs) par cas de test.</GuideStep>
              <GuideStep n={4}>Une fois terminée, la campagne peut être <strong>Archivée</strong> depuis la barre de statut.</GuideStep>
            </ol>
          </CardContent></Card>
        </div>
      </div>

      {/* (4) Référence : statuts ========================================= */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Info className="h-5 w-5 text-violet-500" />Statuts de campagne</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                <tr>{['Statut', 'Libellé', 'Signification'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
                {Object.entries(STATUSES).map(([code, s]) => (
                  <tr key={code} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-2 font-mono text-xs">{code}</td>
                    <td className="px-4 py-2"><Badge color={s.color}>{s.label}</Badge></td>
                    <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{s.desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* (4) Référence : récurrences ===================================== */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><RefreshCw className="h-5 w-5 text-violet-500" />Types de récurrence</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                <tr>{['Code', 'Libellé', 'Description'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
                {RECURRENCES.map((r) => (
                  <tr key={r.value} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-2 font-mono text-xs">{r.value}</td>
                    <td className="px-4 py-2 font-medium">{r.label}</td>
                    <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{r.desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Règles de gestion + Endpoints API */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Règles de gestion</h3>
          <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Le <strong>Nom</strong> est obligatoire. À la création, un identifiant <code className="font-mono text-xs">CPG-XXXXXXXX</code> est généré et le statut est <strong>Brouillon</strong>.</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Une campagne <strong>inactive</strong> n'est pas exécutée (planification ignorée).</li>
            <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />Une campagne <strong>En cours</strong> (IN_PROGRESS) ne peut pas être supprimée.</li>
            <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />Le statut <strong>Archivée</strong> est terminal : aucun changement de statut possible ensuite.</li>
            <li className="flex items-start gap-2"><AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />Une campagne <strong>Annulée</strong> ne peut être que <strong>Archivée</strong>.</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Un cas de test ajouté reçoit automatiquement un <strong>ordre d'exécution</strong> (max + 1) ; il peut être réordonné.</li>
          </ul>
        </CardContent></Card>

        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2"><Search className="h-5 w-5 text-violet-500" />Endpoints API</h3>
          <ul className="space-y-1.5 text-sm font-mono text-gray-700 dark:text-gray-200">
            {[
              ['GET', '#10B981', '/api/automata/campaigns'],
              ['GET', '#10B981', '/api/automata/campaigns/{id}'],
              ['POST', '#3B82F6', '/api/automata/campaigns'],
              ['PUT', '#F59E0B', '/api/automata/campaigns/{id}'],
              ['DELETE', '#EF4444', '/api/automata/campaigns/{id}'],
              ['PATCH', '#8B5CF6', '/api/automata/campaigns/{id}/status?status='],
              ['POST', '#3B82F6', '/api/automata/campaigns/{id}/link-test/{testCaseId}'],
              ['DELETE', '#EF4444', '/api/automata/campaigns/{id}/testcases/{testCaseId}'],
              ['GET', '#10B981', '/api/automata/campaigns/{id}/testcases'],
              ['PUT', '#F59E0B', '/api/automata/campaigns/{id}/testcases/reorder'],
              ['POST', '#3B82F6', '/api/automata/executions/campaigns/{id}/start'],
              ['GET', '#10B981', '/api/automata/executions/campaigns/{id}'],
            ].map((r, i) => (
              <li key={i} className="flex items-center gap-2"><Badge color={r[1]}>{r[0]}</Badge><span className="break-all">{r[2]}</span></li>
            ))}
          </ul>
          <p className="text-xs text-gray-400 mt-3 flex items-center gap-1"><ArrowRight className="h-3 w-3" />Source : automata_campaign / CampaignController + CampaignExecutionController</p>
        </CardContent></Card>
      </div>
    </div>
  );
};

export default CampaignManagerPOC;
